import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import themeConfig from './everestConfig';
import useAppParams from '@/redux/hooks/useAppParams'; // Custom hook to get Redux state

type ThemeContextType = {
  theme: typeof themeConfig;
  setTheme: React.Dispatch<React.SetStateAction<typeof themeConfig>>;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Get appVariant and source from Redux
  const { appVariant, source } = useAppParams();

  // Memoize the theme configuration to avoid unnecessary re-renders
  const dynamicTheme = useMemo(() => {
    return {
      ...themeConfig,
      layout: {
        ...themeConfig.layout,
        backButton: source === 'everestcharge' ? false : true, // Dynamic backButton based on source
      },
    };
  }, [source]); // Only recalculate theme when `source` changes

  const [theme, setTheme] = useState(dynamicTheme);

  // Update the theme whenever dynamicTheme changes
  useEffect(() => {
    setTheme(dynamicTheme);
  }, [dynamicTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
